import { useEffect, useMemo, useState } from "react";

import "../../../styles/panel/user/Commission.css";
import { fetchMyCommissions } from "../../../api/finance";
import { convertToPersianDigits, formatToman } from "../../../utils/stringUtil";
import moment from "jalali-moment";

const MONTHS_FA = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

export default function Commission() {
  const [items, setItems] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");

  useEffect(() => {
    fetchMyCommissions().then((res) => setItems(res.data));
  }, []);

  const rows = useMemo(
    () =>
      items
        .filter((item) => item.owner_phone !== item.creator_phone)
        .map((item) => ({
          id: item.id,
          shopName: item.store_name,
          ownerPhone: item.owner_phone,
          date: moment(item.created_at).format("jYYYY/jMM/jDD"),
          year: moment(item.created_at).format("jYYYY"),
          month: moment(item.created_at).format("jMM"),
          planPrice: Number(item.plan_price || 0),
          commissionPercent: Number(item.commission_rate || 0),
          income: Number(item.commission_amount || 0),
        })),
    [items],
  );

  const years = useMemo(
    () => [...new Set(rows.map((r) => r.year))].sort((a, b) => b - a),
    [rows],
  );
  const filteredRows = useMemo(
    () =>
      rows
        .filter((r) => (yearFilter === "all" ? true : r.year === yearFilter))
        .filter((r) => (monthFilter === "all" ? true : r.month === monthFilter))
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [rows, yearFilter, monthFilter],
  );

  const totalIncome = useMemo(
    () => filteredRows.reduce((sum, r) => sum + r.income, 0),
    [filteredRows],
  );

  return (
    <div className="commission-page">
      <div className="commission-head">
        <div>
          <h1>پورسانت من</h1>
          <p>سود دریافتی شما از سایت‌هایی که برای دیگران ثبت کرده‌اید</p>
        </div>
      </div>

      <div className="commission-toolbar">
        <div className="commission-summary">
          <div className="summary-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="7" width="18" height="12" rx="2" />
              <path d="M3 11h18" />
              <circle
                cx="8"
                cy="15"
                r="1.1"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </div>
          <div className="summary-text">
            <div className="summary-label">جمع کل درآمد شما</div>
            <div className="summary-value">{formatToman(totalIncome)}</div>
          </div>
        </div>

        <div className="commission-filters">
          <div className="filter-field">
            <label htmlFor="year-filter">سال</label>
            <select
              id="year-filter"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="all">همه سال‌ها</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {convertToPersianDigits(y)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="month-filter">ماه</label>
            <select
              id="month-filter"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
            >
              <option value="all">همه ماه‌ها</option>
              {MONTHS_FA.map((m, idx) => {
                const value = String(idx + 1).padStart(2, "0");
                return (
                  <option key={m} value={value}>
                    {m}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      <div className="commission-table-wrap">
        <table className="commission-table">
          <thead>
            <tr>
              <th>#</th>
              <th>نام فروشگاه</th>
              <th>شماره مالک</th>
              <th>تاریخ</th>
              <th>قیمت پلن</th>
              <th>درصد پورسانت</th>
              <th>درآمد شما</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td className="empty-cell" colSpan={7}>
                  در این بازه‌ی زمانی رکوردی یافت نشد.
                </td>
              </tr>
            ) : (
              filteredRows.map((r, i) => (
                <tr key={r.id}>
                  <td>{convertToPersianDigits(i + 1)}</td>
                  <td className="shop-cell">{r.shopName}</td>
                  <td className="ltr-cell">
                    {convertToPersianDigits(r.ownerPhone)}
                  </td>
                  <td>{convertToPersianDigits(r.date)}</td>
                  <td>{`${formatToman(r.planPrice)} تومان`}</td>
                  <td>
                    <span className="percent-badge">
                      {convertToPersianDigits(r.commissionPercent)}٪
                    </span>
                  </td>
                  <td className="income-cell">{`${formatToman(r.income)} تومان`}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
